import { defineRule } from 'vee-validate';

// References:
// creatinine: https://emedicine.medscape.com/article/2054342-overview?reg=1
export const validations = {
  creatinine: {
    'mg/dL': {
      min: 0.1,
      max: 5,
    },
    'umol/L': {
      min: 10,
      // max: 442 - Changed to 999 as requested by UQ
      max: 999,
    },
    'mL/min': {
      min: 1,
      max: 100,
    },
    'L/h': {
      min: 1,
      max: 100,
    },
  },
  age: {
    years: [1, 99],
    days: [0, 365],
  },
  weight: {
    kg: [30, 150],
    lb: [60, 330],
  },
  height: {
    cm: [100, 250],
    in: [30, 100],
  },
  mic: [0.1, 100],
  interval: [1, 24],
  duration: {
    hours: [0.01, 24],
    minutes: [0.1, 60],
  },
};

defineRule('required', (value) => {
  if (value === null || value === undefined || value === '') {
    return 'This field is required';
  }
  return true;
});

defineRule('ageBetween', (value, { unit, model }) => {
  const check = model.age && unit === model.age.unit ? model.age.range : validations.age[unit];
  const valid = check[0] <= parseFloat(value) && parseFloat(value) <= check[1];
  if (!valid) {
    return `Age must be between ${check[0]} and ${check[1]} ${model.age.unit}`;
  }
  return true;
});

defineRule('weightBetween', (value, { unit, model }) => {
  const check =
    model.weight && unit === model.weight.unit ? model.weight.range : validations.weight[unit];
  const valid = check[0] <= parseFloat(value) && parseFloat(value) <= check[1];
  const checkUnit = model.weight ? model.weight.unit : unit;
  if (!valid) {
    return `Weight must be between ${check[0]} and ${check[1]} ${checkUnit}`;
  }
  return true;
});

defineRule('heightBetween', (value, { unit, model }) => {
  const check =
    model.height && unit === model.height.unit ? model.height.range : validations.height[unit];
  const valid = check[0] <= parseFloat(value) && parseFloat(value) <= check[1];
  if (!valid) {
    return `Height must be between ${check[0]} and ${check[1]} ${unit}`;
  }
  return true;
});

defineRule('secrRequired', (value) => {
  if (value === null || value === undefined || value === '') {
    return 'SeCr is required for optimized dosing.';
  }
  return true;
});

defineRule('secrBetween', (value, { unit }) => {
  const v = validations['creatinine'][unit];
  const valid = v.min <= parseFloat(value) && parseFloat(value) <= v.max;
  if (!valid) {
    return `SeCr must be between ${v.min} and ${v.max} ${unit}`;
  }
  return true;
});

defineRule('micBetween', (value) => {
  const v = validations['mic'];
  const valid = v[0] <= parseFloat(value) && parseFloat(value) <= v[1];
  if (!valid) {
    return `MIC must be between ${v[0]} and ${v[1]}`;
  }
  return true;
});

defineRule('targetBetween', (value, { method, unit, minValue, maxValue, drug }) => {
  const v = drug.target.filter((t) => t.type === method)[0];
  const min = v ? v.range[0] : 1;
  const max = v ? v.range[1] : 100;
  let valid = parseFloat(minValue) < parseFloat(maxValue);
  if (!valid) {
    return 'Minimum must be less than maximum value';
  }
  valid = min <= parseFloat(value) && parseFloat(value) <= max;
  if (!valid) {
    return `Target must be between ${min} and ${max} ${unit}`;
  }
  return true;
});

defineRule('intervalBetween', (value) => {
  if (value === null || value === undefined || value === '') return true;
  const v = validations['interval'];
  const valid = v[0] <= parseFloat(value) && parseFloat(value) <= v[1];
  if (!valid) {
    return `Interval must be between ${v[0]} and ${v[1]} hours`;
  }
  return true;
});

defineRule('intervalDuration', (value, { duration, durationUnit, intervalUnit }) => {
  const i = intervalUnit === 'hours' ? value * 1 : value * 60;
  const d = durationUnit === 'hours' ? duration * 1 : duration * 60;
  if (d > parseFloat(i)) {
    return 'Interval must be equal or greater than duration';
  }
  return true;
});

defineRule('durationBetween', (value, { unit }) => {
  if (value === null || value === undefined || value === '') return true;
  const v = validations['duration'];
  const valid = v[unit][0] <= parseFloat(value) && parseFloat(value) <= v[unit][1];
  if (!valid) {
    return `Duration must be between ${v[unit][0]} and ${v[unit][1]} ${unit}`;
  }
  return true;
});

defineRule('doseBetween', (value, { unit, model }) => {
  if (value === null || value === undefined || value === '') return true;
  const convert = unit === 'g' ? 1000 : 1;
  const min = model ? model.default.minDose / convert : 0;
  const max = model ? model.default.maxDose / convert : 5000;
  const valid = min <= parseFloat(value) && parseFloat(value) <= max;
  if (!valid) {
    return `Dose must be between ${min} and ${max} ${unit}`;
  }
  return true;
});

defineRule('between', (value, [min, max]) => {
  if (value === null || value === undefined || value === '') return true;
  const valid = parseFloat(value) >= parseFloat(min) && parseFloat(value) <= parseFloat(max);
  if (!valid) {
    return `Must be between ${min} and ${max}`;
  }
  return true;
});
